import React from 'react';
import { clsx } from 'clsx';
import { PLAY_STORE_URL, APP_STORE_URL } from '../config/appStoreUrls';

export interface FooterLink {
  label: string;
  href: string;
}

export interface FooterColumn {
  title: string;
  links: FooterLink[];
}

export interface FooterSocialLink {
  label: string;
  href: string;
  icon: React.ReactNode;
}

export interface FooterProps {
  /** Logo element or image */
  logo?: React.ReactNode;
  /** Short description below the logo */
  description?: string;
  /** Navigation columns */
  columns?: FooterColumn[];
  /** Social media links with icons */
  socialLinks?: FooterSocialLink[];
  /** Copyright text override */
  copyright?: string;
  className?: string;
}

const defaultColumns: FooterColumn[] = [
  {
    title: 'Product',
    links: [
      { label: 'Benefits', href: '/#benefits' },
      { label: 'How it Works', href: '/#how-it-works' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Hardware', href: '/hardware' },
      { label: 'Documentation', href: '/docs' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', href: '/about' },
      { label: 'Blog', href: '/blog' },
      { label: 'For Operators', href: '/operator' },
    ],
  },
  {
    title: 'Mobile App',
    links: [
      { label: 'Get the App', href: '/app' },
      { label: 'Google Play', href: PLAY_STORE_URL },
      { label: 'App Store', href: APP_STORE_URL },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '/privacy' },
    ],
  },
];

const defaultSocialLinks: FooterSocialLink[] = [
  {
    label: 'GitHub',
    href: 'https://github.com/Cplng',
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
        <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
      </svg>
    ),
  },
];

export const Footer: React.FC<FooterProps> = ({
  logo,
  description = 'Helping local merchants create interconnected reward networks that strengthen communities.',
  columns = defaultColumns,
  socialLinks = defaultSocialLinks,
  copyright = `\u00A9 ${new Date().getFullYear()} Coupling. All rights reserved.`,
  className,
}) => (
  <footer className={clsx('bg-neutral-900 text-white', className)}>
    <div className="container mx-auto px-4 py-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 lg:gap-8">
        {/* Brand column */}
        <div className="lg:col-span-2">
          <div className="flex items-center space-x-3 mb-4">
            {logo || (
              <img
                src="/logo/coupling-logo-mono.svg"
                alt="Coupling"
                className="h-8 w-8"
              />
            )}
            <span className="text-xl font-bold text-white">Coupling</span>
          </div>
          <p className="text-neutral-400 text-sm leading-relaxed max-w-sm mb-6">
            {description}
          </p>
          {socialLinks.length > 0 && (
            <div className="flex items-center space-x-4">
              {socialLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-400 hover:text-white transition-colors"
                  aria-label={link.label}
                >
                  {link.icon}
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Link columns */}
        {columns.map((column) => (
          <div key={column.title}>
            <h3 className="text-sm font-semibold text-neutral-200 uppercase tracking-wider mb-4">
              {column.title}
            </h3>
            <ul className="space-y-3">
              {column.links.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm text-neutral-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>

    {/* Bottom bar */}
    <div className="border-t border-neutral-800">
      <div className="container mx-auto px-4 py-6">
        <p className="text-sm text-neutral-500 text-center">
          {copyright}
        </p>
      </div>
    </div>
  </footer>
);
